import { useState, useCallback, useRef, useEffect, useMemo, Fragment } from "react";
import theme from "../data/theme";
import { TEAM_META, TEAM_HISTORY, STARTERS_2025 } from "../data/projectionData";
import { VEGAS_NOTES, VEGAS_AS_OF } from "../data/vegasNotes";
import TeamEditor from "./TeamEditor";
import { fetchCurrentRosters } from "../utils/rosterUpdate";
import { buildLabStarters, diffStarters } from "../utils/sleeperRosters";
import { getStarters } from "../utils/depthOverrides";
import { downloadJson } from "../utils/downloadJson";
import { buildProjlabExport, importProjlabBackup, safeReadProjlab, writeProjlabSnapshot } from "../utils/projlabStorage";
import { loadAllProjectedPlayers } from "../utils/projlabRankings";
import { pprLabel } from "../utils/projlabProjection";
import { projectionField, invalidateProjectionCache, projectGamesForRow, clearUserTeam, syncAllToHouse } from "../utils/projectionSource";
import { buildDraftValues, draftValuesStorageKey } from "../utils/draftValuesBridge";
import { DEFAULT_SETTINGS } from "../data/scoring";
import { THRESHOLDS, HIST_YEARS, computeHistThresholdCounts, computeProjectedCounts, computeProjectedPaceCounts } from "../utils/realismGuards";
import { QUICK_LEVERS, readLeverValues, applyQuickEdit, commitQuickEdit } from "../utils/projlabQuickEdit";
import { resolvePoolEntry } from "./ProjectionResults";
import { usePlayerCard } from "../context/PlayerCardContext";
import { ActionButton } from "./ui/States";
import { captureProjlabOpened, captureProjlabTeamStarted, captureProjlabRankingsViewed, captureProjlabBuildDraftValues } from "../utils/projlabAnalytics";
import { isQbPremiumFormat } from "../utils/leagueFormat";

const { colors, fonts, spacing, radius, fontSize, fontWeight, helpers } = theme;

// Explicit-snapshot key: the user's projections → personal draft values, frozen
// at "Build Draft Values" click. Stable mid-draft by design (never auto-rebuilt).

// ── team colors ────────────────────────────────────────────────────────────────
// Carefully differentiated — no two teams look the same on a dark background

const TEAM_COLORS = {
  // REDS (by hue: cherry → scarlet → crimson → wine → burgundy)
  KC:  "#FF2545",  // Chiefs — bright cherry (most vivid red)
  SF:  "#E8201A",  // 49ers — scarlet
  ATL: "#E2342D",  // Falcons — true red
  TB:  "#D42020",  // Bucs — darker red
  NE:  "#D63045",  // Patriots — cranberry
  HOU: "#C41230",  // Texans — deep crimson
  ARI: "#C22E48",  // Cardinals — rose-wine
  WAS: "#9B3055",  // Commanders — burgundy-magenta

  // BLUES (by lightness: powder → sky → royal → navy)
  LAC: "#00B4FF",  // Chargers — powder blue (lightest)
  TEN: "#6BAAEE",  // Titans — periwinkle
  DET: "#0098E8",  // Lions — honolulu blue (cyan-shifted)
  DAL: "#4888FF",  // Cowboys — steel blue (lighter, grayer)
  BUF: "#0066FF",  // Bills — royal blue
  NYG: "#2850E0",  // Giants — true blue (slightly purple)
  LA:  "#2040CC",  // Rams — deep blue
  IND: "#1835B0",  // Colts — navy-royal (darkest blue)

  // GREENS
  GB:  "#28A745",  // Packers — classic green
  NYJ: "#22CC55",  // Jets — brighter lime green
  PHI: "#00B890",  // Eagles — teal-green
  SEA: "#69FF47",  // Seahawks — action green (neon)
  JAX: "#00B8A8",  // Jaguars — teal (cyan-shifted from Eagles)
  MIA: "#00E0C0",  // Dolphins — aqua (lightest green)

  // PURPLES
  BAL: "#7B50E0",  // Ravens — bright purple
  MIN: "#9B45D0",  // Vikings — magenta-purple

  // ORANGES
  CHI: "#F26522",  // Bears — orange
  CIN: "#FF5E00",  // Bengals — bright orange
  CLE: "#EB5E28",  // Browns — burnt orange
  DEN: "#FF7B1A",  // Broncos — lighter orange

  // UNIQUE
  PIT: "#FFD020",  // Steelers — gold
  NO:  "#DEB964",  // Saints — muted gold
  LV:  "#B0B7BC",  // Raiders — silver
  CAR: "#00A3E0",  // Panthers — bright cyan
};

// ── division layout ────────────────────────────────────────────────────────────

const DIVISIONS = {
  AFC: {
    East:  ["BUF", "MIA", "NE",  "NYJ"],
    North: ["BAL", "CIN", "CLE", "PIT"],
    South: ["HOU", "IND", "JAX", "TEN"],
    West:  ["DEN", "KC",  "LAC", "LV" ],
  },
  NFC: {
    East:  ["DAL", "NYG", "PHI", "WAS"],
    North: ["CHI", "DET", "GB",  "MIN"],
    South: ["ATL", "CAR", "NO",  "TB" ],
    West:  ["ARI", "LA",  "SEA", "SF" ],
  },
};

// ── module-level derived data ──────────────────────────────────────────────────

const SLOTS = ["QB1", "RB1", "RB2", "WR1", "WR2", "WR3", "TE1"];

function _calcTotal(abbrev) {
  const s = getStarters(abbrev);
  return SLOTS.reduce((sum, slot) => sum + (s[slot]?.stats?.ppr ?? 0), 0);
}

const _sortedTotals = Object.keys(TEAM_META)
  .map(a => ({ abbrev: a, total: _calcTotal(a) }))
  .sort((a, b) => b.total - a.total);

const TEAM_RANKS  = {};
const TEAM_TOTALS = {};
_sortedTotals.forEach(({ abbrev, total }, i) => {
  TEAM_RANKS[abbrev]  = i + 1;
  TEAM_TOTALS[abbrev] = total;
});

const maxTotal = _sortedTotals[0].total;
const minTotal = _sortedTotals[_sortedTotals.length - 1].total;

// ── flat player index for search ───────────────────────────────────────────────

const ALL_PLAYERS = [];
Object.entries(STARTERS_2025).forEach(([abbrev, slots]) => {
  Object.entries(slots).forEach(([slot, entry]) => {
    if (entry?.player) {
      ALL_PLAYERS.push({ player: entry.player, pos: entry.pos || slot.replace(/[0-9+]/g, ""), slot, abbrev, lc: entry.player.toLowerCase() });
    }
  });
});

// ── helpers ────────────────────────────────────────────────────────────────────

function getRankColor(rank) {
  if (rank <= 8)  return colors.status.success;
  if (rank <= 16) return colors.extended.emerald;
  if (rank <= 24) return colors.status.warning;
  return colors.status.danger;
}

const CONF_THRESH = { QB: [280, 200], RB: [220, 120], WR: [200, 120], TE: [160, 90] };

function getConf(ppr, pos) {
  const [hi, lo] = CONF_THRESH[pos] ?? [200, 100];
  return ppr >= hi ? "high" : ppr >= lo ? "med" : "low";
}

function confDotColor(level) {
  if (level === "high") return colors.status.success;
  if (level === "med")  return colors.status.warning;
  return colors.status.danger;
}

// ── PassRunBar ─────────────────────────────────────────────────────────────────

function PassRunBar({ passAtt, rushAtt, passPct }) {
  const pctInt = Math.round(passPct * 100);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: fontSize.caption, color: colors.pos.QB, fontWeight: fontWeight.bold, width: 28, textAlign: "right", fontFamily: fonts.display }}>
          {passAtt}
        </span>
        <div style={{ flex: 1, height: 8, borderRadius: 4, overflow: "hidden", display: "flex", background: "rgba(255,255,255,0.04)" }}>
          <div style={{ width: `${pctInt}%`, height: "100%", background: `linear-gradient(90deg, ${colors.pos.QB}90, ${colors.pos.QB}50)` }} />
          <div style={{ width: `${100 - pctInt}%`, height: "100%", background: `linear-gradient(90deg, ${colors.pos.RB}50, ${colors.pos.RB}90)` }} />
        </div>
        <span style={{ fontSize: fontSize.caption, color: colors.pos.RB, fontWeight: fontWeight.bold, width: 28, fontFamily: fonts.display }}>
          {rushAtt}
        </span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: fontSize.micro, color: colors.text.faint, letterSpacing: "0.5px" }}>PASS</span>
        <span style={{ fontSize: fontSize.micro, color: colors.text.faint, letterSpacing: "0.5px" }}>RUSH</span>
      </div>
    </div>
  );
}

// ── PlayerLine ─────────────────────────────────────────────────────────────────

function PlayerLine({ name, ppr, pos, rank }) {
  const conf = getConf(ppr, pos);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: fontSize.body, lineHeight: "22px" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: confDotColor(conf), flexShrink: 0 }} />
      <span style={{
        color: colors.pos[pos],
        fontWeight: fontWeight.bold,
        fontSize: fontSize.caption,
        width: 26,
        flexShrink: 0,
        letterSpacing: "0.3px",
        fontFamily: fonts.display,
      }}>
        {pos}{rank}
      </span>
      <span style={{
        color: name === "—" ? colors.text.faint : colors.text.primary,
        flex: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: fontWeight.normal,
      }}>
        {name}
      </span>
      <span style={{ color: colors.text.secondary, fontWeight: fontWeight.medium, fontSize: fontSize.small, fontFamily: fonts.display, flexShrink: 0 }}>
        {ppr > 0 ? ppr.toFixed(0) : "—"}
      </span>
    </div>
  );
}

// ── TeamCard ───────────────────────────────────────────────────────────────────

const DEPTH = [
  ["QB1", "QB", 1], ["RB1", "RB", 1], ["RB2", "RB", 2],
  ["WR1", "WR", 1], ["WR2", "WR", 2], ["WR3", "WR", 3],
  ["TE1", "TE", 1],
];

// Step name to index for progress bar segments
// ── PlayerSearch ──────────────────────────────────────────────────────────────

function PlayerSearch({ onSelect, progress }) {
  const [query, setQuery] = useState("");
  const [open, setOpen]   = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);

  const results = useMemo(() => {
    if (query.length < 2) return [];
    const q = query.toLowerCase();
    return ALL_PLAYERS.filter(p => p.lc.includes(q)).slice(0, 8);
  }, [query]);

  const statusBadge = (abbrev) => {
    const s = progress[abbrev];
    if (s?.state === "done") return { label: "Projected", color: "#22c55e", bg: "rgba(34,197,94,0.12)" };
    if (s?.state === "progress") return { label: "In Progress", color: "#fbbf24", bg: "rgba(251,191,36,0.12)" };
    return { label: "Not Started", color: colors.text.faint, bg: "rgba(255,255,255,0.06)" };
  };

  return (
    <div ref={ref} style={{ position: "relative", maxWidth: 420 }}>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: fontSize.sub, color: colors.text.faint, pointerEvents: "none" }}>⌕</span>
        <input
          type="text" value={query} placeholder="Search players..."
          onChange={e => { setQuery(e.target.value); setOpen(e.target.value.length >= 2); }}
          onFocus={() => { if (query.length >= 2) setOpen(true); }}
          style={{
            width: "100%", padding: "8px 12px 8px 30px", background: colors.bg.raised,
            border: `1px solid ${open ? colors.border.default : colors.border.subtle}`,
            borderRadius: radius.md, color: colors.text.primary, fontSize: fontSize.body,
            fontFamily: fonts.body, outline: "none", transition: "border 0.15s",
          }}
        />
      </div>
      {open && results.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, zIndex: 50,
          background: colors.bg.surface, border: `1px solid ${colors.border.default}`,
          borderRadius: radius.lg, boxShadow: "0 8px 32px rgba(0,0,0,0.5)", overflow: "hidden",
        }}>
          {results.map((p, i) => {
            const tc = TEAM_COLORS[p.abbrev] || colors.text.muted;
            const badge = statusBadge(p.abbrev);
            return (
              <div key={i} onClick={() => { onSelect(p.abbrev); setQuery(""); setOpen(false); }}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", cursor: "pointer", borderBottom: i < results.length - 1 ? `1px solid ${colors.border.subtle}` : "none", transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.heavy, color: tc, background: `${tc}15`, padding: "2px 6px", borderRadius: 4, fontFamily: fonts.display, minWidth: 24, textAlign: "center" }}>{p.pos}</span>
                <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.medium, color: colors.text.primary, flex: 1 }}>{p.player}</span>
                <span style={{ fontSize: fontSize.caption, fontWeight: fontWeight.bold, color: tc, fontFamily: fonts.display }}>{p.abbrev}</span>
                <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: badge.color, background: badge.bg, padding: "2px 7px", borderRadius: 8, fontFamily: fonts.display }}>{badge.label}</span>
              </div>
            );
          })}
        </div>
      )}
      {open && query.length >= 2 && results.length === 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, zIndex: 50,
          background: colors.bg.surface, border: `1px solid ${colors.border.default}`,
          borderRadius: radius.lg, padding: "12px 16px", textAlign: "center",
          fontSize: fontSize.small, color: colors.text.faint, boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        }}>
          No players found
        </div>
      )}
    </div>
  );
}

const STEP_LABELS = ["Plays", "Split", "Alloc", "Eff"];

function MiniProgress({ completedSteps, tc }) {
  return (
    <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
      {STEP_LABELS.map((l, i) => (
        <div key={i} style={{ width: 16, height: 4, borderRadius: 2, background: completedSteps?.[i] ? tc : "rgba(255,255,255,0.08)", opacity: completedSteps?.[i] ? 0.7 : 1, transition: "background 0.2s" }} title={l} />
      ))}
    </div>
  );
}

// How long a "RESET?" stays armed before it forgets it was asked.
const RESET_ARM_MS = 3000;

function TeamCard({ abbrev, onClick, isSelected, status, onResetTeam }) {
  const [hov, setHov] = useState(false);
  const [armed, setArmed] = useState(false);
  const armTimer = useRef(null);
  const tc    = TEAM_COLORS[abbrev];
  const meta  = TEAM_META[abbrev];
  const name  = meta?.fullName || abbrev;
  const vegas = VEGAS_NOTES[abbrev];
  const isDone = status?.state === "done";
  const isProgress = status?.state === "progress";

  const disarm = useCallback(() => {
    clearTimeout(armTimer.current);
    armTimer.current = null;
    setArmed(false);
  }, []);
  useEffect(() => () => clearTimeout(armTimer.current), []);

  // Two deliberate clicks, no native dialog — window.confirm is not used anywhere
  // in this app. The arm expires (and drops when the pointer leaves the card) so
  // a forgotten arm can never turn a later stray click into a delete.
  const handleReset = (e) => {
    e.stopPropagation();  // never let the reset open the editor
    if (!armed) {
      setArmed(true);
      clearTimeout(armTimer.current);
      armTimer.current = setTimeout(() => setArmed(false), RESET_ARM_MS);
      return;
    }
    disarm();
    onResetTeam(abbrev);
  };

  return (
    <div
      onClick={() => onClick(abbrev)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => { setHov(false); disarm(); }}
      style={{
        background: isSelected ? "rgba(255,107,0,0.08)" : `${tc}08`,
        borderTop: `1px solid ${isSelected ? "rgba(255,107,0,0.4)" : colors.border.subtle}`,
        borderRight: `1px solid ${isSelected ? "rgba(255,107,0,0.4)" : colors.border.subtle}`,
        borderBottom: `1px solid ${isSelected ? "rgba(255,107,0,0.4)" : colors.border.subtle}`,
        borderLeft: `3px solid ${tc}`,
        borderRadius: radius.lg,
        padding: "10px 14px 10px 12px",
        cursor: "pointer",
        transition: "all 0.18s ease",
        transform: hov ? "translateX(2px)" : "translateX(0)",
        opacity: isDone ? 0.8 : 1,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span style={{ fontFamily: fonts.display, fontWeight: fontWeight.heavy, fontSize: fontSize.sub, color: colors.text.primary, letterSpacing: "0.5px", flexShrink: 0 }}>
        {abbrev}
      </span>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <span style={{ fontSize: fontSize.small, color: colors.text.muted, fontWeight: fontWeight.normal, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {name}
        </span>
        {vegas && (
          <span style={{ fontSize: fontSize.nano, color: colors.text.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            2025 {vegas.rec2025} · Vegas {vegas.impliedPPG.toFixed(1)} PPG
          </span>
        )}
      </div>
      {isProgress && (
        <>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: tc, flexShrink: 0, animation: "pulse-dot 1.8s ease-in-out infinite", boxShadow: `0 0 6px ${tc}60` }} />
          <MiniProgress completedSteps={status?.completedSteps} tc={tc} />
        </>
      )}
      {isDone && (
        <div style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(34,197,94,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: fontSize.caption, color: "#22c55e", fontWeight: fontWeight.black }}>✓</span>
        </div>
      )}
      {(isDone || isProgress) && onResetTeam && (
        <button
          onClick={handleReset}
          title={`Discard your ${abbrev} projection and use Mock1 house`}
          style={{
            background: "transparent",
            border: "none",
            padding: "0 2px",
            fontFamily: fonts.display,
            // nano is reserved for uppercase letter-spaced labels (theme.js) — the
            // bare glyph takes micro so it stays legible.
            fontSize: armed ? fontSize.nano : fontSize.micro,
            fontWeight: fontWeight.bold,
            letterSpacing: "0.5px",
            lineHeight: 1,
            color: armed ? tc : colors.text.faint,
            cursor: "pointer",
            flexShrink: 0,
            opacity: hov ? 1 : 0,
            pointerEvents: hov ? "auto" : "none",
            transition: "opacity 0.18s ease",
          }}
        >
          {armed ? "RESET?" : "↺"}
        </button>
      )}
      {!isProgress && !isDone && (
        <span style={{ fontSize: fontSize.small, color: hov ? tc : "transparent", transition: "color 0.18s ease", flexShrink: 0 }}>→</span>
      )}
    </div>
  );
}

// ── DetailSheet ────────────────────────────────────────────────────────────────

const DETAIL_SLOTS = [
  { slot: "QB1", pos: "QB", rank: 1 },
  { slot: "RB1", pos: "RB", rank: 1 },
  { slot: "RB2", pos: "RB", rank: 2 },
  { slot: "WR1", pos: "WR", rank: 1 },
  { slot: "WR2", pos: "WR", rank: 2 },
  { slot: "WR3", pos: "WR", rank: 3 },
  { slot: "TE1", pos: "TE", rank: 1 },
];

const CONF_LEGEND = [
  { label: "Stable",       color: colors.status.success },
  { label: "Uncertain",    color: colors.status.warning },
  { label: "Needs review", color: colors.status.danger  },
];

function DetailSheet({ abbrev, onClose, liveTeamStarters, onEdit }) {
  const meta      = TEAM_META[abbrev];
  const vegas     = VEGAS_NOTES[abbrev];
  const hist2025  = (TEAM_HISTORY[abbrev] || []).find(h => h.year === 2025);
  const starters  = getStarters(abbrev);
  const total     = TEAM_TOTALS[abbrev];
  const rank      = TEAM_RANKS[abbrev];
  const rankColor = getRankColor(rank);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: helpers.overlay(0.65),
        backdropFilter: "blur(6px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: colors.bg.surface,
          borderLeft: `5px solid ${meta.primaryColor}`,
          borderRadius: radius.xl,
          padding: `${spacing.xxl}px ${spacing.xxxl}px`,
          minWidth: 420,
          maxWidth: 460,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
        }}
      >
        {/* Header row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: spacing.lg }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
              <h2 style={{ fontFamily: fonts.display, fontSize: fontSize.display, fontWeight: fontWeight.black, color: colors.text.primary, margin: 0, letterSpacing: "1px" }}>
                {abbrev}
              </h2>
              <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.heavy, color: rankColor, background: `${rankColor}18`, padding: "2px 8px", borderRadius: radius.md, fontFamily: fonts.display }}>
                #{rank}
              </span>
            </div>
            <p style={{ fontSize: fontSize.caption, color: colors.text.muted, margin: `${spacing.xs}px 0 0` }}>
              <span style={{ color: rankColor, fontWeight: fontWeight.heavy, fontSize: fontSize.sub }}>{total.toFixed(0)}</span>
              <span style={{ marginLeft: spacing.xs }}>Total Team Fantasy Points — 2025 Actual</span>
            </p>
            {vegas && (
              <div style={{ marginTop: spacing.xs }}>
                <p style={{ fontSize: fontSize.caption, color: colors.text.muted, margin: 0 }}>
                  <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.heavy, color: colors.text.faint, letterSpacing: "0.5px", fontFamily: fonts.display, marginRight: spacing.xs }}>’26 VEGAS OUTLOOK</span>
                  2025 {vegas.rec2025} · {vegas.impliedPPG.toFixed(1)} Vegas implied PPG · {vegas.projWins} proj wins
                </p>
                <p style={{ fontSize: fontSize.nano, color: colors.text.faint, fontStyle: "italic", margin: "2px 0 0" }}>
                  as of {VEGAS_AS_OF}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: colors.border.subtle, border: `1px solid ${colors.border.default}`, borderRadius: radius.md, color: colors.text.muted, fontSize: fontSize.heading, cursor: "pointer", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.display, flexShrink: 0 }}
          >
            ×
          </button>
        </div>

        {/* Pass/Run split */}
        {hist2025 && (
          <div style={{ marginBottom: spacing.lg }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: spacing.xs }}>
              <span style={{ fontSize: fontSize.micro, color: colors.text.faint, fontWeight: fontWeight.heavy, letterSpacing: "1px", fontFamily: fonts.display }}>PASS / RUN SPLIT</span>
              <span style={{ fontSize: fontSize.micro, color: colors.text.faint }}>{hist2025.totalPlays.toLocaleString()} total plays</span>
            </div>
            <PassRunBar passAtt={hist2025.passAtt} rushAtt={hist2025.rushAtt} passPct={hist2025.passPct} />
          </div>
        )}

        {/* Depth chart */}
        <div style={{ fontSize: fontSize.micro, fontWeight: fontWeight.heavy, color: colors.text.faint, letterSpacing: "1px", marginBottom: spacing.sm, fontFamily: fonts.display }}>
          DEPTH CHART
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          {DETAIL_SLOTS.map(({ slot, pos, rank: posRank }) => {
            const entry = starters[slot];
            const ppr   = entry?.stats?.ppr ?? 0;
            const name  = (liveTeamStarters?.[slot] ?? entry)?.player ?? "—";
            const conf  = getConf(ppr, pos);
            return (
              <div key={slot} style={{ display: "flex", alignItems: "center", gap: spacing.md, padding: `${spacing.md}px ${spacing.lg}px`, borderRadius: radius.md, background: colors.bg.raised, border: `1px solid ${colors.border.subtle}` }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: confDotColor(conf), flexShrink: 0 }} />
                <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.heavy, color: colors.pos[pos], background: `${colors.pos[pos]}15`, padding: "2px 6px", borderRadius: radius.sm, minWidth: 30, textAlign: "center", fontFamily: fonts.display }}>
                  {pos}{posRank}
                </span>
                <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.medium, color: entry ? colors.text.primary : colors.text.faint, flex: 1 }}>
                  {name}
                </span>
                <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.heavy, color: entry ? colors.brand.accent : colors.text.faint, fontFamily: fonts.display, flexShrink: 0 }}>
                  {ppr > 0 ? ppr.toFixed(1) : "—"}
                </span>
              </div>
            );
          })}
        </div>

        {/* Confidence legend */}
        <div style={{ display: "flex", gap: spacing.xl, marginTop: spacing.lg, paddingTop: spacing.md, borderTop: `1px solid ${colors.border.subtle}` }}>
          {CONF_LEGEND.map(({ label, color }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: spacing.xs, fontSize: fontSize.micro, color: colors.text.muted, fontFamily: fonts.display }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
              {label}
            </div>
          ))}
        </div>

        {/* Edit button — re-open this team in the editor (resumes at its saved step) */}
        <button
          onClick={onEdit}
          style={{ width: "100%", marginTop: spacing.lg, padding: `${spacing.lg}px 0`, background: colors.brand.gradientBtn, border: "none", borderRadius: radius.xl, color: colors.text.primary, fontFamily: fonts.display, fontSize: fontSize.sub, fontWeight: fontWeight.heavy, letterSpacing: "1.5px", cursor: "pointer", boxShadow: helpers.brandGlow(0.25) }}
        >
          EDIT 2026 PROJECTIONS →
        </button>
      </div>
    </div>
  );
}

// ── ProjectionLab (main) ───────────────────────────────────────────────────────

// ── RosterDiffModal ────────────────────────────────────────────────────────────

function RosterDiffModal({ diffData, rookieCount, onConfirm, onCancel }) {
  const hasChanges = diffData.some(t => t.added.length || t.removed.length || t.changed.length);
  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
        display: "flex", justifyContent: "center", alignItems: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: colors.bg.surface, borderRadius: radius.xl,
          border: `1px solid ${colors.border.default}`,
          padding: "24px 28px", width: "100%", maxWidth: 560,
          maxHeight: "80vh", overflowY: "auto",
          boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontFamily: fonts.display, fontSize: fontSize.heading, fontWeight: fontWeight.black, letterSpacing: "1px", color: colors.text.primary }}>
            ROSTER REFRESH
          </h2>
          <button onClick={onCancel} style={{ background: "transparent", border: "none", color: colors.text.muted, fontSize: fontSize.title, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ fontSize: fontSize.small, color: colors.text.secondary, marginBottom: 16, display: "flex", gap: 16 }}>
          <span>{diffData.length} team{diffData.length !== 1 ? "s" : ""} with changes</span>
          {rookieCount > 0 && (
            <span style={{ color: colors.status.warning, fontWeight: fontWeight.bold }}>
              ★ {rookieCount} rookie{rookieCount !== 1 ? "s" : ""} detected
            </span>
          )}
          {!hasChanges && <span style={{ color: colors.status.success }}>No lineup changes detected</span>}
        </div>

        {hasChanges && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
            {diffData.map(({ team, added, removed, changed }) => (
              <div key={team} style={{
                background: colors.bg.raised, borderRadius: radius.md,
                border: `1px solid ${colors.border.subtle}`, padding: "10px 14px",
                borderLeft: `4px solid ${TEAM_COLORS[team] || colors.border.default}`,
              }}>
                <div style={{ fontFamily: fonts.display, fontWeight: fontWeight.heavy, fontSize: fontSize.sub, color: colors.text.primary, marginBottom: 6 }}>
                  {team}
                </div>
                {changed.map(c => (
                  <div key={c.slot} style={{ fontSize: fontSize.caption, color: colors.text.secondary, marginBottom: 2 }}>
                    <span style={{ color: colors.pos[c.pos] || colors.text.muted, fontWeight: fontWeight.bold, marginRight: 6 }}>{c.slot}</span>
                    <span style={{ color: colors.status.danger, textDecoration: "line-through", marginRight: 4 }}>{c.from}</span>
                    <span style={{ color: colors.text.faint, marginRight: 4 }}>→</span>
                    <span style={{ color: colors.status.success, fontWeight: fontWeight.medium }}>{c.to}</span>
                  </div>
                ))}
                {added.map(a => (
                  <div key={a.slot} style={{ fontSize: fontSize.caption, color: colors.status.success, marginBottom: 2 }}>
                    <span style={{ fontWeight: fontWeight.bold, marginRight: 6 }}>{a.slot}</span>+ {a.player}
                  </div>
                ))}
                {removed.map(r => (
                  <div key={r.slot} style={{ fontSize: fontSize.caption, color: colors.status.danger, marginBottom: 2 }}>
                    <span style={{ fontWeight: fontWeight.bold, marginRight: 6 }}>{r.slot}</span>− {r.player}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            style={{ padding: "8px 20px", background: "transparent", border: `1px solid ${colors.border.default}`, borderRadius: radius.md, color: colors.text.secondary, fontFamily: fonts.display, fontSize: fontSize.caption, fontWeight: fontWeight.bold, cursor: "pointer", letterSpacing: "0.5px" }}
          >
            CANCEL
          </button>
          <ActionButton
            onClick={onConfirm}
            tone="brand"
            disabled={!hasChanges}
            style={{ padding: "8px 20px" }}
          >
            APPLY ROSTERS
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

// ── RankingsView ──────────────────────────────────────────────────────────────

const POS_TABS = ["QB", "RB", "WR", "TE"];

const RANK_COLS = {
  QB:  [{ k: "passYds", l: "Pass Yds" }, { k: "passTD", l: "Pass TD" }, { k: "int", l: "INT" }, { k: "rushYds", l: "Rush Yds" }],
  RB:  [{ k: "rushYds", l: "Rush Yds" }, { k: "rushTD", l: "Rush TD" }, { k: "rec", l: "Rec" }, { k: "recYds", l: "Rec Yds" }],
  WR:  [{ k: "rec", l: "Rec" }, { k: "recYds", l: "Rec Yds" }, { k: "recTD", l: "Rec TD" }, { k: "targets", l: "Tgt" }],
  TE:  [{ k: "rec", l: "Rec" }, { k: "recYds", l: "Rec Yds" }, { k: "recTD", l: "Rec TD" }, { k: "targets", l: "Tgt" }],
};

const STAT_LABELS = { passYds: "pass yds", passTD: "pass TD", rushYds: "rush yds", recYds: "rec yds", rec: "receptions" };
const labelForThreshold = (t) => `${t.min}+ ${STAT_LABELS[t.stat] || t.stat}`;

const LEVER_LABELS = {
  rushPct:   "Rush Share %",
  tgtPct:    "Target Share %",
  games:     "Games",
  catchPct:  "Catch %",
  ypr:       "Yds/Rec",
  recTDPct:  "Rec TD %",
  ypc:       "Yds/Carry",
  rushTDPct: "Rush TD %",
  intPct:    "INT %",
  rushYPC:   "Rush Yds/Carry (QB)",
};
const LEVER_STEP = {
  rushPct: 1, tgtPct: 1, games: 1, catchPct: 1, recTDPct: 1, rushTDPct: 1, intPct: 1,
  ypr: 0.1, ypc: 0.1, rushYPC: 0.1,
};

function LeverStepper({ leverKey, value, onChange }) {
  const step = LEVER_STEP[leverKey] ?? 1;
  const dec = step < 1 ? 1 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0" }}>
      <span style={{ fontFamily: fonts.display, fontSize: fontSize.body, color: colors.text.secondary }}>
        {LEVER_LABELS[leverKey] || leverKey}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button
          onClick={() => onChange(Math.round((value - step) * 10) / 10)}
          style={{ width: 22, height: 22, background: "rgba(255,255,255,0.06)", border: `1px solid ${colors.border.subtle}`, borderRadius: radius.sm, color: colors.text.secondary, cursor: "pointer", fontSize: fontSize.small, lineHeight: 1 }}
        >
          −
        </button>
        <span style={{ fontFamily: fonts.display, fontSize: fontSize.body, fontWeight: fontWeight.bold, color: colors.text.primary, minWidth: 40, textAlign: "center" }}>
          {value.toFixed(dec)}
        </span>
        <button
          onClick={() => onChange(Math.round((value + step) * 10) / 10)}
          style={{ width: 22, height: 22, background: "rgba(255,255,255,0.06)", border: `1px solid ${colors.border.subtle}`, borderRadius: radius.sm, color: colors.text.secondary, cursor: "pointer", fontSize: fontSize.small, lineHeight: 1 }}
        >
          +
        </button>
      </div>
    </div>
  );
}

// Quick-edit lever panel — not the full wizard editor. Seeds lever values
// from the team's saved projection, applies pending changes headlessly on
// every stepper click for a live pts/teamTotal preview, and only writes to
// localStorage when SAVE is clicked (CANCEL discards `changes`/`preview`).
function QuickEditModal({ target, onClose, onSaved, scoring }) {
  const [values, setValues] = useState(null);
  const [changes, setChanges] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!target) { setValues(null); setChanges({}); setPreview(null); return; }
    setValues(readLeverValues({ abbrev: target.team, slot: target.slot, pos: target.pos }));
    setChanges({});
    setPreview(null);
  }, [target]);

  if (!target || !values) return null;

  const levers = QUICK_LEVERS[target.pos] || [];

  const handleChange = (key, newValue) => {
    const nextChanges = { ...changes, [key]: newValue };
    setChanges(nextChanges);
    setPreview(applyQuickEdit({ abbrev: target.team, slot: target.slot, changes: nextChanges, scoring }));
  };

  const newPlayerPts = preview?.updatedSave.effSummary.players.find(p => p.slot === target.slot)?.pts;
  const newTeamTotal = preview?.updatedSave.effSummary.teamTotal;

  const handleSave = () => {
    if (preview) commitQuickEdit(target.team, preview.updatedSave, preview.entries);
    onSaved();
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={onClose}
    >
      <div
        style={{ background: colors.bg.surface, borderRadius: radius.lg, border: `1px solid ${colors.border.default}`, padding: 24, minWidth: 340 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontFamily: fonts.display, fontSize: fontSize.sub, fontWeight: fontWeight.black, color: colors.text.primary }}>
            {target.player}
          </span>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: colors.text.muted, fontSize: fontSize.sub, cursor: "pointer", lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
        <div style={{ fontFamily: fonts.display, fontSize: fontSize.caption, color: colors.text.faint, marginBottom: 12 }}>
          {target.team} · {target.pos} · {target.slot}
        </div>

        <div style={{ borderTop: `1px solid ${colors.border.subtle}`, borderBottom: `1px solid ${colors.border.subtle}` }}>
          {levers.map(l => (
            <LeverStepper
              key={l.key}
              leverKey={l.key}
              value={changes[l.key] !== undefined ? changes[l.key] : values[l.key]}
              onChange={(v) => handleChange(l.key, v)}
            />
          ))}
        </div>

        <div style={{ marginTop: 12, marginBottom: 16, fontFamily: fonts.display, fontSize: fontSize.body, color: colors.text.secondary }}>
          <div>
            This player: {target.pts.toFixed(1)} → <span style={{ color: colors.brand.accent, fontWeight: fontWeight.heavy }}>{(newPlayerPts ?? target.pts).toFixed(1)}</span> pts
          </div>
          <div style={{ fontSize: fontSize.caption, color: colors.text.faint, marginTop: 2 }}>
            Team total: {newTeamTotal != null ? newTeamTotal.toFixed(1) : "—"} <span style={{ color: colors.text.muted }}>· {pprLabel(scoring)}</span>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: `1px solid ${colors.border.default}`, borderRadius: radius.md, color: colors.text.secondary, fontSize: fontSize.small, fontWeight: fontWeight.bold, fontFamily: fonts.display, padding: "6px 14px", cursor: "pointer", letterSpacing: "0.5px" }}
          >
            CANCEL
          </button>
          <button
            onClick={handleSave}
            style={{ background: colors.brand.accent, border: "none", borderRadius: radius.md, color: colors.bg.surface, fontSize: fontSize.small, fontWeight: fontWeight.bold, fontFamily: fonts.display, padding: "6px 14px", cursor: "pointer", letterSpacing: "0.5px" }}
          >
            SAVE
          </button>
        </div>
      </div>
    </div>
  );
}

function RankingsView({ progress, onClose, scoring, settings, activeLeagueName }) {
  const { openPlayerCard } = usePlayerCard();
  const [posTab, setPosTab] = useState("QB");
  const [realismOpen, setRealismOpen] = useState(true);
  const [sortCol, setSortCol] = useState("PPR");
  const [sortDir, setSortDir] = useState("desc");
  const [quickEditTarget, setQuickEditTarget] = useState(null);
  const [bumpKey, setBumpKey] = useState(0);
  const [toast, setToast] = useState(null);

  const allPlayers = useMemo(() => loadAllProjectedPlayers(progress, scoring), [progress, bumpKey, scoring]);

  // Read-only, whole-league field for the realism check: real saved data for
  // completed teams, default-seed projections filling in every other team -
  // never written back anywhere, doesn't touch the main rankings table above.
  const doneTeams = useMemo(
    () => new Set(Object.entries(progress || {}).filter(([, p]) => p.state === "done").map(([abbrev]) => abbrev)),
    [progress]
  );
  const compositeField = useMemo(() => {
    // ONE call, no merge. projectionField already resolves per TEAM — the user's
    // save where they have one, the house set elsewhere, plus the market curve for
    // players on no depth chart at all.
    //
    // It used to be `projectionField({ excludeTeams: doneTeams })` merged with
    // allPlayers, which was correct only while allPlayers held done teams ONLY.
    // Now that allPlayers covers all 32, that merge would double-count every
    // house team, so the exclusion and the merge both go. Same rule, one source.
    //
    // This is the same layer usePricing.getPlayerProj reads, and that is the
    // point: My Value below is built from this field, the results page from
    // getPlayerProj, so the two agree on a player BY CONSTRUCTION.
    return projectionField();
    // bumpKey/progress are deliberate cache-busting keys, not data this memo
    // reads: projectionField() goes to localStorage for the user's saves, and
    // these two are the signals that a save just changed. eslint cannot see a
    // dependency that lives outside the closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bumpKey, progress]);

  // Explicit snapshot: freeze the current composite field into personal draft
  // values under the app league context. Never auto-runs — the user clicks, so
  // values stay stable mid-draft. Re-clicking overwrites the single snapshot.
  const handleBuildValues = useCallback(() => {
    const bridgeSettings = {
      teams: settings?.teams ?? DEFAULT_SETTINGS.teams,
      budget: settings?.budget ?? DEFAULT_SETTINGS.budget,
      positions: settings?.positions ?? DEFAULT_SETTINGS.positions,
      scoring,
    };
    // QB-premium effective replacement is waiver-tier, so bench demand is ON
    // there — 2QB for the same reason superflex is: the second mandatory
    // starter pushes replacement past the starters-only pool. SINGLE-QB
    // leagues keep starters-only replacement — validated against Smithtown
    // 2022-25 prices; do not change their behavior.
    const { values, meta } = buildDraftValues(compositeField, bridgeSettings, {
      benchDemand: isQbPremiumFormat(bridgeSettings.positions),
    });
    const snapshot = {
      builtAt: new Date().toISOString(),
      league: activeLeagueName,
      teams: meta.teams,
      budget: meta.budget,
      scoringLabel: meta.scoringLabel,
      values,
    };
    // Per-league key so building values in one league never overwrites another.
    try { localStorage.setItem(draftValuesStorageKey(activeLeagueName), JSON.stringify(snapshot)); } catch {}
    captureProjlabBuildDraftValues({ teamsProjected: doneTeams.size });
    setToast(`Built ${Object.keys(values).length} draft values · ${meta.scoringLabel}`);
  }, [compositeField, scoring, settings, activeLeagueName, doneTeams]);

  // Auto-dismiss the confirmation toast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const grouped = useMemo(() => {
    const g = { QB: [], RB: [], WR: [], TE: [] };
    allPlayers.forEach(p => {
      const pos = p.pos;
      if (g[pos]) g[pos].push(p);
    });
    // Sort each position by PPR desc
    Object.values(g).forEach(arr => arr.sort((a, b) => b.pts - a.pts));
    return g;
  }, [allPlayers]);

  const list = useMemo(() => grouped[posTab] || [], [grouped, posTab]);
  const cols = RANK_COLS[posTab];

  // Stable PPR-based position rank for the # column - keyed off `list`, which
  // is always PPR-desc (grouped's own sort), independent of whatever column
  // the table is currently displayed sorted by.
  const pprRankMap = useMemo(() => {
    const m = new Map();
    list.forEach((p, i) => m.set(`${p.team}-${p.slot}`, i + 1));
    return m;
  }, [list]);

  const colValue = (p, key) => {
    if (key === "PPR") return p.pts;
    // 🔴 His OWN games. Every row here carries the games its `pts` were
    // scored over (loadAllProjectedPlayers), and dividing by a literal 17
    // instead made this column ~12% low AND disagreed with the identical
    // number in Team Builder and Draft Plan. Sorting on it was wrong the same
    // way: a 12-game player ranked as though he had played 17.
    if (key === "PPR_PER_G") return p.pts / projectGamesForRow(p);
    return p.stats?.[key] ?? 0;
  };

  const sortedList = useMemo(() => {
    const arr = [...list];
    arr.sort((a, b) => {
      const av = colValue(a, sortCol), bv = colValue(b, sortCol);
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return arr;
  }, [list, sortCol, sortDir]);

  const handleSort = (key) => {
    if (key === sortCol) setSortDir(d => (d === "desc" ? "asc" : "desc"));
    else { setSortCol(key); setSortDir("desc"); }
  };

  const thStyle = { fontSize: fontSize.micro, fontWeight: fontWeight.heavy, color: colors.text.faint, letterSpacing: "1px", fontFamily: fonts.display, padding: "6px 8px", textAlign: "right", borderBottom: `1px solid ${colors.border.subtle}`, whiteSpace: "nowrap" };
  const tdStyle = { fontSize: fontSize.body, fontWeight: fontWeight.medium, color: colors.text.secondary, fontFamily: fonts.display, padding: "7px 8px", textAlign: "right", borderBottom: `1px solid ${colors.border.subtle}` };

  return (
    <div style={{ maxWidth: 1600, margin: "0 auto", padding: "0 24px 28px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <h2 style={{ margin: 0, fontFamily: fonts.display, fontSize: fontSize.title, fontWeight: fontWeight.black, letterSpacing: "2px", color: colors.text.primary }}>
            PROJECTED RANKINGS
          </h2>
          <span style={{ fontSize: fontSize.caption, fontWeight: fontWeight.bold, color: colors.brand.accent, letterSpacing: "0.5px", fontFamily: fonts.display }}>pts · {pprLabel(scoring)}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={handleBuildValues}
            title="Freeze the current projections into personal draft values (localStorage snapshot)"
            style={{ background: colors.brand.accent, border: "none", borderRadius: radius.md, color: colors.bg.surface, fontSize: fontSize.small, fontWeight: fontWeight.bold, fontFamily: fonts.display, padding: "6px 14px", cursor: "pointer", letterSpacing: "0.5px" }}
          >
            BUILD DRAFT VALUES
          </button>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: `1px solid ${colors.border.default}`, borderRadius: radius.md, color: colors.text.secondary, fontSize: fontSize.small, fontWeight: fontWeight.bold, fontFamily: fonts.display, padding: "6px 14px", cursor: "pointer", letterSpacing: "0.5px" }}
          >
            ← TEAMS
          </button>
        </div>
      </div>

      {/* Position tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        {POS_TABS.map(pos => {
          const active = posTab === pos;
          const pc = colors.pos[pos];
          return (
            <button
              key={pos}
              onClick={() => { setPosTab(pos); setSortCol("PPR"); setSortDir("desc"); }}
              style={{
                padding: "6px 18px",
                background: active ? `${pc}20` : "rgba(255,255,255,0.04)",
                border: `1px solid ${active ? pc : colors.border.subtle}`,
                borderRadius: radius.md,
                color: active ? pc : colors.text.muted,
                fontFamily: fonts.display,
                fontSize: fontSize.small,
                fontWeight: fontWeight.heavy,
                letterSpacing: "1px",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {pos} <span style={{ fontWeight: fontWeight.medium, opacity: 0.7 }}>({grouped[pos].length})</span>
            </button>
          );
        })}
      </div>

      {/* Realism check — read-only sanity bands vs. real historical counts */}
      {(() => {
        const posThresholds = THRESHOLDS[posTab] || [];
        const histRows = computeHistThresholdCounts();
        const projRows = computeProjectedCounts(compositeField);
        const pacedRows = computeProjectedPaceCounts(compositeField);
        const keyFor = (pos, stat, min) => `${pos}|${stat}|${min}`;
        const histMap = new Map(histRows.map(r => [keyFor(r.pos, r.stat, r.min), r]));
        const projMap = new Map(projRows.map(r => [keyFor(r.pos, r.stat, r.min), r]));
        const paceProjMap = new Map(pacedRows.map(r => [keyFor(r.pos, r.stat, r.min), r]));
        const hasPaceRow = posThresholds.some(t => t.flagLow);

        return (
          <div style={{ background: colors.bg.surface, borderRadius: radius.lg, border: `1px solid ${colors.border.subtle}`, marginBottom: 16, overflow: "hidden" }}>
            <div
              onClick={() => setRealismOpen(o => !o)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", cursor: "pointer", background: "rgba(255,255,255,0.02)" }}
            >
              <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.heavy, letterSpacing: "1.5px", fontFamily: fonts.display, color: colors.text.secondary }}>
                REALISM CHECK {realismOpen ? "▾" : "▸"}
              </span>
              <span style={{ fontSize: fontSize.caption, color: colors.text.faint, fontFamily: fonts.display }}>
                {doneTeams.size > 0
                  ? `${doneTeams.size} of 32 teams are yours — the rest use Mock1 house projections`
                  : "Mock1 house projections — edit anything to make it yours"}
              </span>
            </div>
            {realismOpen && (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ padding: "6px 16px", textAlign: "left", fontSize: fontSize.micro, fontWeight: fontWeight.heavy, letterSpacing: "1px", color: colors.text.faint, fontFamily: fonts.display }}>THRESHOLD</th>
                    {HIST_YEARS.map(y => (
                      <th key={y} style={{ padding: "6px 10px", textAlign: "right", fontSize: fontSize.micro, fontWeight: fontWeight.heavy, letterSpacing: "1px", color: colors.text.faint, fontFamily: fonts.display }}>
                        '{String(y).slice(2)}
                      </th>
                    ))}
                    <th style={{ padding: "6px 10px", textAlign: "right", fontSize: fontSize.micro, fontWeight: fontWeight.heavy, letterSpacing: "1px", color: colors.text.faint, fontFamily: fonts.display }}>AVG</th>
                    <th style={{ padding: "6px 16px", textAlign: "right", fontSize: fontSize.micro, fontWeight: fontWeight.heavy, letterSpacing: "1px", color: colors.text.faint, fontFamily: fonts.display }}>2026 PROJ</th>
                    <th style={{ padding: "6px 16px", width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {posThresholds.map((t, i) => {
                    const key = keyFor(posTab, t.stat, t.min);
                    const hist = histMap.get(key);
                    const proj = projMap.get(key)?.count ?? 0;
                    const over = !!hist && proj > hist.maxCt;
                    const rowColor = over ? "#ef4444" : colors.text.muted;
                    const showPace = !!t.flagLow && !!hist;
                    const paceProj = paceProjMap.get(key)?.count ?? 0;
                    const paceLow = showPace && paceProj < hist.paceMin;
                    return (
                      <Fragment key={key}>
                        <tr style={{ borderTop: i > 0 ? `1px solid ${colors.border.subtle}` : "none" }}>
                          <td style={{ padding: "6px 16px", fontSize: fontSize.body, color: colors.text.secondary, fontFamily: fonts.display }}>
                            {labelForThreshold(t)}
                          </td>
                          {HIST_YEARS.map(y => {
                            const yc = hist?.perYear.find(p => p.year === y)?.count ?? 0;
                            return (
                              <td key={y} style={{ padding: "6px 10px", textAlign: "right", fontSize: fontSize.small, color: colors.text.muted, fontFamily: fonts.display }}>
                                {yc}
                              </td>
                            );
                          })}
                          <td style={{ padding: "6px 10px", textAlign: "right", fontSize: fontSize.body, fontWeight: fontWeight.heavy, color: colors.text.secondary, fontFamily: fonts.display }}>
                            {hist ? hist.avg.toFixed(1) : "—"}
                          </td>
                          <td style={{ padding: "6px 16px", textAlign: "right", fontSize: fontSize.body, fontWeight: fontWeight.heavy, color: rowColor, fontFamily: fonts.display }}>
                            {proj}
                          </td>
                          <td style={{ padding: "6px 16px", textAlign: "right", width: 70 }}>
                            {over && <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.black, color: "#ef4444" }}>⚠ OVER</span>}
                          </td>
                        </tr>
                        {showPace && (
                          <tr style={{ background: "rgba(255,255,255,0.015)" }}>
                            <td style={{ padding: "3px 16px 5px 28px", fontSize: fontSize.caption, fontStyle: "italic", color: colors.text.faint, fontFamily: fonts.display }}>
                              pace
                            </td>
                            {HIST_YEARS.map(y => {
                              const yc = hist.pacePerYear?.find(p => p.year === y)?.count ?? 0;
                              return (
                                <td key={y} style={{ padding: "3px 10px 5px", textAlign: "right", fontSize: fontSize.caption, color: colors.text.faint, fontFamily: fonts.display }}>
                                  {yc}
                                </td>
                              );
                            })}
                            <td style={{ padding: "3px 10px 5px", textAlign: "right", fontSize: fontSize.caption, color: colors.text.faint, fontFamily: fonts.display }}>
                              {hist.paceAvg.toFixed(1)}
                            </td>
                            <td style={{ padding: "3px 16px 5px", textAlign: "right", fontSize: fontSize.caption, fontWeight: fontWeight.heavy, color: paceLow ? "#fbbf24" : colors.text.faint, fontFamily: fonts.display }}>
                              {paceProj}
                            </td>
                            <td style={{ padding: "3px 16px 5px", textAlign: "right", width: 70 }}>
                              {paceLow && <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.black, color: "#fbbf24" }}>LOW</span>}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
            {hasPaceRow && realismOpen && (
              <div style={{ padding: "6px 16px 10px", fontSize: fontSize.micro, color: colors.text.faint, fontFamily: fonts.display, fontStyle: "italic" }}>
                PACE = per-17-games basis (removes health luck from the comparison)
              </div>
            )}
          </div>
        );
      })()}

      {list.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: colors.text.faint, fontSize: fontSize.sub }}>
          No {posTab} projections available.
        </div>
      ) : (
        <div style={{ background: colors.bg.surface, borderRadius: radius.lg, border: `1px solid ${colors.border.subtle}`, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 42 }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: 50 }} />
              {cols.map((_, i) => <col key={i} style={{ width: "auto" }} />)}
              <col style={{ width: 65 }} />
              <col style={{ width: 55 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: "center" }}>#</th>
                <th style={{ ...thStyle, textAlign: "left" }}>PLAYER</th>
                <th style={{ ...thStyle, textAlign: "center" }}>TEAM</th>
                {cols.map(c => (
                  <th key={c.k} onClick={() => handleSort(c.k)} style={{ ...thStyle, cursor: "pointer", userSelect: "none" }}>
                    {c.l.toUpperCase()}{sortCol === c.k ? (sortDir === "desc" ? " ▾" : " ▴") : ""}
                  </th>
                ))}
                <th onClick={() => handleSort("PPR")} style={{ ...thStyle, cursor: "pointer", userSelect: "none" }}>
                  PPR{sortCol === "PPR" ? (sortDir === "desc" ? " ▾" : " ▴") : ""}
                </th>
                <th onClick={() => handleSort("PPR_PER_G")} style={{ ...thStyle, cursor: "pointer", userSelect: "none" }}>
                  PPR/G{sortCol === "PPR_PER_G" ? (sortDir === "desc" ? " ▾" : " ▴") : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedList.map((p) => {
                const tc = TEAM_COLORS[p.team] || colors.text.muted;
                const pprRank = pprRankMap.get(`${p.team}-${p.slot}`) ?? 0;
                const isTop3 = pprRank <= 3;
                const poolEntry = resolvePoolEntry(p);
                return (
                  <tr
                    key={`${p.team}-${p.slot}`}
                    onClick={() => setQuickEditTarget(p)}
                    style={{ background: isTop3 ? "rgba(255,255,255,0.02)" : "transparent", cursor: "pointer" }}
                  >
                    <td style={{ ...tdStyle, textAlign: "center", color: isTop3 ? colors.brand.accent : colors.text.faint, fontWeight: fontWeight.black, fontSize: fontSize.small }}>
                      {pprRank}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>
                      <span style={{ color: colors.text.primary, fontWeight: fontWeight.bold, fontSize: fontSize.body }}>{p.player}</span>
                      {/* Row click opens QuickEdit, so game logs get their own small button */}
                      {poolEntry && (
                        <span
                          title="Game logs"
                          onClick={e => { e.stopPropagation(); openPlayerCard(poolEntry.name, {}, "projection_lab"); }}
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", marginLeft: 6, width: 16, height: 16, borderRadius: 4, verticalAlign: "middle", background: "rgba(255,255,255,0.08)", color: "#64748b", fontSize: 10, fontWeight: 700, lineHeight: 1, cursor: "pointer", transition: "background 0.15s ease, color 0.15s ease" }}
                          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,0,0.2)"; e.currentTarget.style.color = "#FF6B00"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "#64748b"; }}
                        >≡</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.heavy, color: tc, background: `${tc}15`, padding: "2px 6px", borderRadius: 4, fontFamily: fonts.display }}>{p.team}</span>
                    </td>
                    {cols.map(c => (
                      <td key={c.k} style={tdStyle}>
                        {p.stats?.[c.k] != null ? Math.round(p.stats[c.k]).toLocaleString() : "—"}
                      </td>
                    ))}
                    <td style={{ ...tdStyle, color: colors.brand.accent, fontWeight: fontWeight.heavy, fontSize: fontSize.sub }}>
                      {p.pts.toFixed(1)}
                    </td>
                    <td style={{ ...tdStyle, color: colors.text.muted, fontSize: fontSize.caption }}>
                      {(p.pts / projectGamesForRow(p)).toFixed(1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <QuickEditModal
        target={quickEditTarget}
        onClose={() => setQuickEditTarget(null)}
        onSaved={() => { invalidateProjectionCache(); setQuickEditTarget(null); setBumpKey(k => k + 1); }}
        scoring={scoring}
      />
      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: colors.bg.surface, border: `1px solid ${colors.brand.accent}`, borderRadius: radius.md, padding: "10px 18px", color: colors.text.primary, fontFamily: fonts.display, fontSize: fontSize.small, fontWeight: fontWeight.bold, letterSpacing: "0.5px", boxShadow: "0 6px 20px rgba(0,0,0,0.4)", zIndex: 2000 }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
}

// ── CSV Export ────────────────────────────────────────────────────────────────

const POS_ORDER = { QB: 0, RB: 1, WR: 2, TE: 3 };
const SLOT_ORDER = { QB1: 0, RB1: 1, RB2: 2, WR1: 3, WR2: 4, WR3: 5, TE1: 6 };

function exportProjectionsCSV(progress, scoring) {
  const players = loadAllProjectedPlayers(progress, scoring);
  // Sort by team alpha, then position order, then slot order
  players.sort((a, b) => {
    const tc = a.team.localeCompare(b.team);
    if (tc !== 0) return tc;
    const pc = (POS_ORDER[a.pos] ?? 9) - (POS_ORDER[b.pos] ?? 9);
    if (pc !== 0) return pc;
    return (SLOT_ORDER[a.slot] ?? 9) - (SLOT_ORDER[b.slot] ?? 9);
  });

  const esc = v => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const n = (v, dec) => v != null && v !== 0 ? (dec ? Number(v).toFixed(dec) : Math.round(v)) : "";
  const isQB = pos => pos === "QB";

  const header = ["Team","Position","Slot","Player","Games","RushAtt","RushYds","RushTD","Tgt","Rec","RecYds","RecTD","PPR","PPR_PerGame","Comp","PassAtt","PassYds","PassTD","INT"];
  const rows = [header.join(",")];

  players.forEach(p => {
    const s = p.stats || {};
    rows.push([
      p.team,
      p.pos,
      p.slot,
      esc(p.player),
      // The row's real games, not a hardcoded 17 — the column is named Games
      // and every consumer of this CSV divides PPR by it.
      projectGamesForRow(p),
      n(s.rushAtt),
      n(s.rushYds),
      n(s.rushTD),
      isQB(p.pos) ? "" : n(s.targets),
      isQB(p.pos) ? "" : n(s.rec),
      isQB(p.pos) ? "" : n(s.recYds),
      isQB(p.pos) ? "" : n(s.recTD),
      n(p.pts, 1),
      n(p.pts / projectGamesForRow(p), 1),
      isQB(p.pos) ? n(s.comp) : "",
      isQB(p.pos) ? n(s.passAtt) : "",
      isQB(p.pos) ? n(s.passYds) : "",
      isQB(p.pos) ? n(s.passTD) : "",
      isQB(p.pos) ? n(s.int) : "",
    ].join(","));
  });

  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "mock1-projections-2026.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ── ProjectionLab (main) ───────────────────────────────────────────────────────

function loadProgress() {
  const progress = {};
  Object.keys(TEAM_META).forEach(abbrev => {
    try {
      const saved = safeReadProjlab(abbrev);
      if (!saved) return;
      const steps = saved.completedSteps || [false, false, false, false];
      const allDone = steps.every(Boolean);
      const anyDone = steps.some(Boolean);
      if (allDone) progress[abbrev] = { state: "done", completedSteps: steps, currentStep: saved.currentStep };
      else if (anyDone) progress[abbrev] = { state: "progress", completedSteps: steps, currentStep: saved.currentStep };
    } catch {}
  });
  return progress;
}

export default function ProjectionLab({ onBack, settings, activeLeagueName }) {
  // The Lab inherits the app-wide league scoring — no separate selector. With no
  // league selected, settings carries DEFAULT_SETTINGS scoring (0.5 PPR), which
  // is also the app default; league selection re-scores points/ranks only, never
  // any projection seed (allocs/rates/volume are lens-invariant).
  const scoring = settings?.scoring ?? DEFAULT_SETTINGS.scoring;
  const [selected,       setSelected]       = useState(null);
  const [editingTeam,    setEditingTeam]     = useState(null);
  const [liveStarters,   setLiveStarters]   = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [diffData,       setDiffData]       = useState(null);  // null = modal closed
  const [pendingStarters, setPendingStarters] = useState(null);
  const [progress, setProgress] = useState(loadProgress);
  const [showRankings, setShowRankings] = useState(false);

  // Analytics: Lab opened (once on mount), a team started whenever the editor
  // opens for a team, and the rankings view whenever it opens.
  useEffect(() => { captureProjlabOpened(); }, []);
  useEffect(() => { if (editingTeam) captureProjlabTeamStarted({ team: editingTeam }); }, [editingTeam]);
  useEffect(() => { if (showRankings) captureProjlabRankingsViewed(); }, [showRankings]);

  // Auto-snapshot all projlab keys once on Lab entry (rolling, keeps last 3).
  // Runs after loadProgress() above, so any corrupt key was already quarantined.
  useEffect(() => { writeProjlabSnapshot(); }, []);

  const handleStepChange = useCallback((abbrev, step) => {
    setProgress(prev => {
      let saved;
      saved = safeReadProjlab(abbrev);
      const steps = saved?.completedSteps || [false, false, false, false];
      const allDone = steps.every(Boolean);
      const anyDone = steps.some(Boolean) || step !== "1a";
      const state = allDone ? "done" : anyDone ? "progress" : undefined;
      if (!state) return prev;
      const existing = prev[abbrev];
      if (
        existing &&
        existing.state === state &&
        existing.currentStep === step &&
        Array.isArray(existing.completedSteps) &&
        existing.completedSteps.length === steps.length &&
        existing.completedSteps.every((v, i) => v === steps[i])
      ) {
        return prev;
      }
      return { ...prev, [abbrev]: { state, completedSteps: steps, currentStep: step } };
    });
  }, []);

  const doneCount = Object.values(progress).filter(p => p.state === "done").length;

  // ── Reset one team to the Mock1 house projections ──
  // clearUserTeam does the removal AND the cache invalidation; dropping the
  // team's progress entry is what flips the card back to house styling and keeps
  // the "N of 32" counts honest.
  const handleResetTeam = useCallback((abbrev) => {
    clearUserTeam(abbrev);
    setProgress(prev => {
      if (!(abbrev in prev)) return prev;
      const next = { ...prev };
      delete next[abbrev];
      return next;
    });
  }, []);

  // ── Sync the WHOLE Lab to the current Mock1 house set ──
  // Two clicks, like the per-team ↺, and the first one takes the backup: the
  // downloaded file IS the undo path (⬆ IMPORT restores all three stores it
  // covers), so it is not optional and not a separate button the user might skip.
  const [syncArmed, setSyncArmed] = useState(false);
  const [syncFlash, setSyncFlash] = useState(null);
  const syncFlashTimer = useRef(null);
  useEffect(() => () => clearTimeout(syncFlashTimer.current), []);

  const handleArmSync = () => {
    handleExportProjections();   // backup first, always
    setSyncArmed(true);
  };
  const handleConfirmSync = () => {
    const { teams, playerEdits } = syncAllToHouse();
    setProgress({});             // every card back to house styling, counts reset
    setSyncArmed(false);
    setSyncFlash(
      `All 32 teams now use Mock1 house projections — cleared ${teams} team save${teams === 1 ? "" : "s"}`
      + `${playerEdits ? ` and ${playerEdits} player rate edit${playerEdits === 1 ? "" : "s"}` : ""}`
      + ` (backup in your downloads).`
    );
    clearTimeout(syncFlashTimer.current);
    syncFlashTimer.current = setTimeout(() => setSyncFlash(null), 9000);
  };

  // ── Projection backup: download all projlab-* team keys as one JSON ──
  const handleExportProjections = () => {
    const payload = buildProjlabExport();
    const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    downloadJson(payload, `projections-backup-${date}.json`);
  };

  // ── Projection restore: read a backup file, write keys back (skip + report
  //    any team that fails to parse — never partial-wipe the rest). ──
  const importInputRef = useRef(null);
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const report = importProjlabBackup(parsed);
        // A restore rewrites projlab-* wholesale, so the projection layer's user
        // index is stale by definition. Invalidate BEFORE the state update that
        // triggers the re-render, or the first frame reads the old saves.
        invalidateProjectionCache();
        setProgress(loadProgress()); // refresh the picker with restored teams
        const skipMsg = report.skipped.length
          ? `\nSkipped ${report.skipped.length}: ${report.skipped.map(s => `${s.key} (${s.reason})`).join(", ")}`
          : "";
        window.alert(`Imported ${report.imported} team projection(s).${skipMsg}`);
      } catch (err) {
        window.alert(`Import failed — nothing was changed.\n${err?.message || err}`);
      }
    };
    reader.onerror = () => window.alert("Could not read that file.");
    reader.readAsText(file);
  };

  const handleRefreshRosters = async () => {
    setRefreshLoading(true);
    try {
      await fetchCurrentRosters();           // update localStorage cache
      const newStarters = await buildLabStarters({ forceRefresh: true });
      const diff = diffStarters(STARTERS_2025, newStarters);
      const rookieCount = Object.values(newStarters)
        .flatMap(team => Object.values(team))
        .filter(slot => slot?.isRookie).length;
      setPendingStarters(newStarters);
      setDiffData({ teams: diff, rookieCount });
    } catch (err) {
      console.error("[ProjectionLab] Roster refresh failed:", err);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleConfirmRosters = () => {
    setLiveStarters(pendingStarters);
    setDiffData(null);
    setPendingStarters(null);
  };

  const handleCancelDiff = () => {
    setDiffData(null);
    setPendingStarters(null);
  };

  if (editingTeam) {
    return (
      <>
        {/* Keep TeamEditor mounted (just hidden) while rankings show, so its
            in-progress step state is preserved and restored on close. */}
        <div style={{ display: showRankings ? "none" : "block" }}>
          <TeamEditor
            key={editingTeam}
            abbrev={editingTeam}
            teamColor={TEAM_COLORS[editingTeam]}
            onBack={() => { invalidateProjectionCache(); setEditingTeam(null); setProgress(loadProgress()); }}
            onProjectTeam={(team) => { invalidateProjectionCache(); setProgress(loadProgress()); setEditingTeam(team); }}
            startersOverride={liveStarters?.[editingTeam] ?? null}
            onStepChange={(step) => handleStepChange(editingTeam, step)}
            progress={progress}
            onOpenRankings={() => setShowRankings(true)}
            scoring={scoring}
          />
        </div>
        {/* Rankings overlays the editor (renders in flow under the app nav,
            matching grid-mode rankings); its own "← TEAMS" button closes it. */}
        {showRankings && (
          <div style={{ paddingTop: 20 }}>
            <RankingsView progress={progress} onClose={() => setShowRankings(false)} scoring={scoring} settings={settings} activeLeagueName={activeLeagueName} />
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${colors.bg.deep} 50%, ${colors.bg.app} 100%)`, fontFamily: fonts.display, color: colors.text.primary }}>

      {/* Sticky top bar */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, background: colors.bg.deep, borderBottom: `1px solid ${colors.border.subtle}`, padding: `${spacing.md}px ${spacing.xxxl}px`, display: "flex", alignItems: "center", gap: spacing.xl }}>
        <button
          onClick={onBack}
          style={{ background: "transparent", color: colors.text.secondary, border: `1px solid ${colors.border.default}`, padding: `6px ${spacing.lg}px`, fontSize: fontSize.small, fontWeight: fontWeight.bold, fontFamily: fonts.display, letterSpacing: "1px", borderRadius: radius.md, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
        >
          ← Back
        </button>
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <h1 style={{ margin: 0, fontSize: fontSize.heading, fontWeight: fontWeight.black, letterSpacing: "2px", background: colors.brand.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            PROJECTION LAB
          </h1>
          <span style={{ fontSize: fontSize.caption, color: colors.text.muted, letterSpacing: "0.5px" }}>Pick a team to project. You'll set volume allocations, assign efficiency rates, and see projected stats output for each starter.</span>
        </div>
        <ActionButton
          onClick={handleRefreshRosters}
          loading={refreshLoading}
          tone={liveStarters ? "success" : "default"}
          style={{ marginLeft: "auto", padding: "7px 16px" }}
        >
          {liveStarters ? "↻ Rosters Live" : "↻ Refresh Rosters"}
        </ActionButton>
      </div>

      {/* Search + Progress bar */}
      <div style={{ maxWidth: 1600, margin: "0 auto", padding: "16px 24px 0" }}>
        <div style={{ marginBottom: 12 }}>
          <PlayerSearch onSelect={setEditingTeam} progress={progress} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: doneCount > 0 ? colors.status.success : colors.text.muted, fontFamily: fonts.display, flexShrink: 0 }}>
            {doneCount} of 32 projected
          </span>
          <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${(doneCount / 32) * 100}%`, background: "linear-gradient(90deg, #22c55e, #4ade80)", borderRadius: 3, transition: "width 0.3s ease" }} />
          </div>
          {doneCount > 0 && <span style={{ fontSize: fontSize.caption, color: colors.text.faint, fontFamily: fonts.display, flexShrink: 0 }}>{Math.round((doneCount / 32) * 100)}%</span>}
          {/* Un-gated from `doneCount > 0` deliberately. The Rankings pool is now
              populated on a fresh profile (all 32 teams resolve to the house set
              until the user edits one), so gating this button meant the most
              useful screen for a brand-new user was not merely a click away — it
              did not render at all. This is the minimal fix for "reachable in <=1
              click": the button already sits on the Lab's entry header, so
              un-gating it makes Rankings one click and TEAMS one click back.
              Deliberately NOT auto-opening on Rankings for fresh profiles — that
              would displace the team grid, which is still the primary call to
              action, to save a single click. */}
          {(
            <button
              onClick={() => setShowRankings(r => !r)}
              style={{
                padding: "5px 14px",
                background: showRankings ? "rgba(255,107,0,0.12)" : "rgba(255,255,255,0.04)",
                border: `1px solid ${showRankings ? "rgba(255,107,0,0.4)" : colors.border.default}`,
                borderRadius: radius.md,
                color: showRankings ? colors.brand.accent : colors.text.secondary,
                fontFamily: fonts.display,
                fontSize: fontSize.caption,
                fontWeight: fontWeight.bold,
                letterSpacing: "0.5px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
                transition: "all 0.15s",
              }}
            >
              {showRankings ? "← TEAMS" : "RANKINGS"}
            </button>
          )}
          {doneCount > 0 && (
            <ActionButton onClick={() => exportProjectionsCSV(progress, scoring)} style={{ padding: "5px 14px" }}>
              EXPORT CSV
            </ActionButton>
          )}
          <ActionButton
            onClick={handleExportProjections}
            title="Download all Projection Lab data as a JSON backup file"
            style={{ padding: "5px 14px" }}
          >
            ⬇ EXPORT PROJECTIONS
          </ActionButton>
          <ActionButton
            onClick={() => importInputRef.current?.click()}
            title="Restore projections from a backup JSON file"
            style={{ padding: "5px 14px" }}
          >
            ⬆ IMPORT PROJECTIONS
          </ActionButton>
          {!syncArmed ? (
            <ActionButton
              onClick={handleArmSync}
              title="Replace all your Lab projections with the current Mock1 house set (downloads a backup first)"
              style={{ padding: "5px 14px" }}
            >
              ⟲ SYNC TO HOUSE
            </ActionButton>
          ) : (
            // Inline confirm — the same no-native-dialog pattern as the per-team
            // ↺ arm. window.confirm is not used anywhere in this app.
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px", borderRadius: radius.md, border: `1px solid ${colors.status.warning}55`, background: `${colors.status.warning}12` }}>
              <span style={{ fontSize: fontSize.caption, color: colors.text.secondary }}>
                Backup downloaded. Replace ALL your Lab projections with current Mock1 house?
              </span>
              <ActionButton onClick={handleConfirmSync} tone="danger" style={{ padding: "4px 12px" }}>
                REPLACE
              </ActionButton>
              <ActionButton onClick={() => setSyncArmed(false)} style={{ padding: "4px 12px" }}>
                CANCEL
              </ActionButton>
            </span>
          )}
          {syncFlash && (
            <span style={{ fontSize: fontSize.caption, color: colors.status.success, fontWeight: fontWeight.bold }}>
              {syncFlash}
            </span>
          )}
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: "none" }}
            onChange={handleImportFile}
          />
        </div>
      </div>

      {/* Rankings view OR conference grids */}
      {showRankings ? (
        <RankingsView progress={progress} onClose={() => setShowRankings(false)} scoring={scoring} settings={settings} activeLeagueName={activeLeagueName} />
      ) : (
      <div style={{ maxWidth: 1600, margin: "0 auto", padding: "12px 24px 28px" }}>
        {Object.entries(DIVISIONS).map(([conf, divs]) => (
          <div key={conf} style={{ marginBottom: 28 }}>
            {/* Conference header */}
            <div style={{
              fontSize: fontSize.hero,
              fontWeight: fontWeight.black,
              color: conf === "AFC" ? "#60a5fa" : "#f87171",
              letterSpacing: "5px",
              fontFamily: fonts.display,
              marginBottom: 14,
              paddingBottom: 8,
              borderBottom: `2px solid ${conf === "AFC" ? "rgba(96,165,250,0.25)" : "rgba(248,113,113,0.25)"}`,
            }}>
              {conf}
            </div>

            {/* 4-column division grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
              {Object.entries(divs).map(([div, teams]) => {
                const divDone = teams.every(tm => progress[tm]?.state === "done");
                return (
                <div key={div}>
                  {/* Division label */}
                  <div style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: colors.text.faint, letterSpacing: "2px", marginBottom: 8, textAlign: "center", fontFamily: fonts.display, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    {div.toUpperCase()}
                    {divDone && (
                      <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.heavy, color: "#2dd4bf", background: "rgba(45,212,191,0.12)", border: "1px solid rgba(45,212,191,0.2)", padding: "1px 7px", borderRadius: 10, letterSpacing: "0.5px", lineHeight: "16px" }}>COMPLETE</span>
                    )}
                  </div>
                  {/* Team cards stacked */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {teams.map(tm => (
                      <TeamCard
                        key={tm}
                        abbrev={tm}
                        onClick={setEditingTeam}
                        isSelected={selected === tm}
                        status={progress[tm]}
                        onResetTeam={handleResetTeam}
                      />
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Detail sheet overlay */}
      {selected && (
        <DetailSheet abbrev={selected} onClose={() => setSelected(null)} onEdit={() => { const t = selected; setSelected(null); setEditingTeam(t); }} liveTeamStarters={liveStarters?.[selected]} />
      )}

      {/* Roster diff modal */}
      {diffData && (
        <RosterDiffModal
          diffData={diffData.teams}
          rookieCount={diffData.rookieCount}
          onConfirm={handleConfirmRosters}
          onCancel={handleCancelDiff}
        />
      )}
    </div>
  );
}
